import { Component, OnDestroy, OnInit, Optional, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { of, Subject, zip } from 'rxjs';
import { map, switchMap, takeUntil } from 'rxjs/operators';

import { ProfileStore } from '@fry/lib/profile';
import { SecurityService } from '@fry/lib/security';
import { User, UserService } from '@fry/lib/users';

import { UserLanguage, UserLanguageService } from './user-language.service';
import { UserLanguageEditComponent } from '../user-language-edit/user-language-edit.component';


@Component({
    selector: 'eas-user-language',
    templateUrl: './user-language.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserLanguageComponent implements OnInit, OnDestroy {
  private destroyed$ = new Subject<void>();

  availableLanguages: UserLanguage[];
  preferredLanguage: UserLanguage;
  user: User;
  canEditPreferredLanguage = false;

  constructor(
    private dialog: MatDialog,
    private userLanguageService: UserLanguageService,
    private profile: ProfileStore,
    private security: SecurityService,
    @Optional() private userService: UserService
  ) {

  }

  ngOnInit() {
    this.availableLanguages = this.userLanguageService.availableLanguages;

    const user$ = this.userService ? of(this.userService.user) : this.profile.getCurrent();

    user$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        user => {
          this.setUser(user);
          this.setCanEditPreferredLanguage(user);
        }
      );
  }

  private setUser(user: User) {
    this.user = user;
    this.preferredLanguage = this.userLanguageService.getPreferredLanguage(user);
  }

  private setCanEditPreferredLanguage(user: User) {
    zip(this.profile.getCurrent(), of(user)).pipe(
      map(
        ([currentUser, user]) => {
          if (currentUser.doc._id == user.doc._id) {
            return this.security.hasPermission('users.editPreferredLanguage.own');
          } else {
            return this.security.hasPermission('users.edit');
          }
        }
      ),
      switchMap(hasPermission => hasPermission)
    ).subscribe(
      canEditPreferredLanguage =>
        this.canEditPreferredLanguage = canEditPreferredLanguage
    );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  changeLanguage() {
    this.dialog.open(UserLanguageEditComponent, {
      closeOnNavigation: true,
      disableClose: true,
      hasBackdrop: true,
      minWidth: '50vw',
      data: {
        user: this.user
      }
    }).afterClosed().subscribe((user: User|undefined) => {
      if (user !== undefined) {
        this.setUser(user);
      }
    });
  }
}
